import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Grid, Button, Switch, Divider, InputLabel, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import i18next from "i18next";

import { useCreateCoupon } from "../../hooks/coupons/useCreateCoupon";
import { useUpdateCoupon } from "../../hooks/coupons/useUpdateCoupon";
import FormInput from "../inputs/FormInput";
import LanguageToggle from "../global/LanguageToggle";
import useCurrency from "../../hooks/common/useCurrency";

const FormAddEditCoupon = ({ currentData, isEdit = false, onSubmitSuccess }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const initialLang = localStorage.getItem("i18nextLng") || i18next.language || "ar";
    const [activeLang, setActiveLang] = useState(initialLang.startsWith("ar") ? "ar" : "en");
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    const { currency } = useCurrency();

    // Form States
    const [code, setCode] = useState("");
    const [type, setType] = useState("percent");
    const [value, setValue] = useState("");
    const [minOrderTotal, setMinOrderTotal] = useState("");
    const [maxDiscount, setMaxDiscount] = useState("");
    const [usageLimit, setUsageLimit] = useState("");
    const [usageLimitPerUser, setUsageLimitPerUser] = useState("");
    const [startsAt, setStartsAt] = useState("");
    const [endsAt, setEndsAt] = useState("");
    const [isActive, setIsActive] = useState(true);

    // Hooks
    const { createCouponMutate, isCreating } = useCreateCoupon();
    const { updateCouponMutate, isUpdating } = useUpdateCoupon();

    const loadingSubmit = isCreating || isUpdating;

    // Initialize form data for edit mode
    useEffect(() => {
        if (isEdit && currentData) {
            setCode(currentData.code || "");
            setType(currentData.type || "percent");
            setValue(currentData.value || "");
            setMinOrderTotal(currentData.min_order_total || "");
            setMaxDiscount(currentData.max_discount || "");
            setUsageLimit(currentData.usage_limit || "");
            setUsageLimitPerUser(currentData.usage_limit_per_user || "");
            setStartsAt(currentData.starts_at ? currentData.starts_at.substring(0, 10) : "");
            setEndsAt(currentData.ends_at ? currentData.ends_at.substring(0, 10) : "");
            setIsActive(currentData.is_active !== undefined ? currentData.is_active : true);
        }
    }, [isEdit, currentData]);

    const SubmitForm = (e) => {
        e.preventDefault();

        const formData = {
            code,
            type,
            value: parseFloat(value),
            min_order_total: minOrderTotal ? parseFloat(minOrderTotal) : null,
            max_discount: maxDiscount ? parseFloat(maxDiscount) : null,
            usage_limit: usageLimit ? parseInt(usageLimit) : null,
            usage_limit_per_user: usageLimitPerUser ? parseInt(usageLimitPerUser) : null,
            starts_at: startsAt || null,
            ends_at: endsAt || null,
            is_active: isActive,
        };

        if (isEdit) {
            updateCouponMutate({ id: currentData.id, data: formData }, {
                onSuccess: () => {
                    if (onSubmitSuccess) onSubmitSuccess();
                }
            });
        } else {
            createCouponMutate(formData, {
                onSuccess: () => {
                    if (onSubmitSuccess) onSubmitSuccess();
                }
            });
        }
    };

    const typeOptions = [
        { id: 'percent', name: { en: 'Percentage', ar: 'نسبة مئوية' } },
        { id: 'fixed', name: { en: 'Fixed Amount', ar: 'مبلغ ثابت' } },
    ];

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, md: 4 },
                mt: 2,
                direction: isArabic ? "rtl" : "ltr",
                backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "#FFFFFF",
                borderRadius: 4,
                border: `1px solid ${theme.palette.divider}`,
            }}
        >
            <Box component="form" onSubmit={SubmitForm} sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
                    <Box>
                        <Typography variant="h5" fontWeight="bold" color="primary">
                            {isEdit
                                ? (isArabic ? "تعديل الكوبون" : "Edit Coupon")
                                : (isArabic ? "إضافة كوبون جديد" : "Add New Coupon")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {isArabic ? "قم بتعبئة قواعد وخصومات الكوبون" : "Fill in the coupon rules and discounts"}
                        </Typography>
                    </Box>
                    <LanguageToggle activeLang={activeLang} onChange={(lng) => setActiveLang(lng)} showLabel={true} />
                </Box>

                <Grid container spacing={4}>
                    {/* Basic Info Section */}
                    <Grid item xs={12}>
                        <Typography variant="overline" color="text.secondary" fontWeight="bold">
                            {isArabic ? "البيانات الأساسية" : "Basic Information"}
                        </Typography>
                        <Divider sx={{ mb: 3, mt: 1 }} />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <FormInput
                            label={isArabic ? "كود الكوبون" : "Coupon Code"}
                            type="text"
                            value={code}
                            onChange={(e) => setCode(e.target.value.toUpperCase())}
                            placeholder={isArabic ? "مثال: SAVE20" : "e.g: SAVE20"}
                            required
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <FormInput
                            label={isArabic ? "نوع الخصم" : "Discount Type"}
                            type="select"
                            value={type}
                            onChange={(e) => setType(e.target.value)}
                            options={typeOptions}
                            required
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <FormInput
                            label={isArabic ? "قيمة الخصم" : "Discount Value"}
                            type="number"
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            placeholder={type === 'percent' ? "e.g: 20" : "e.g: 5.000"}
                            InputProps={{
                                startAdornment: type === 'percent' ? '%' : currency,
                            }}
                            required
                        />
                    </Grid>

                    {/* Limits Section */}
                    <Grid item xs={12}>
                        <Typography variant="overline" color="text.secondary" fontWeight="bold">
                            {isArabic ? "حدود الاستخدام والميزانية" : "Usage Limits & Budget"}
                        </Typography>
                        <Divider sx={{ mb: 3, mt: 1 }} />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "الحد الأدنى للطلب" : "Minimum Order Total"}
                            type="number"
                            value={minOrderTotal}
                            onChange={(e) => setMinOrderTotal(e.target.value)}
                            placeholder="e.g: 20.00"
                            InputProps={{
                                startAdornment: currency,
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "أقصى مبلغ للخصم" : "Maximum Discount Amount"}
                            type="number"
                            value={maxDiscount}
                            onChange={(e) => setMaxDiscount(e.target.value)}
                            placeholder="e.g: 50.00"
                            InputProps={{
                                startAdornment: currency,
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "إجمالي حد الاستخدام" : "Total Usage Limit"}
                            type="number"
                            value={usageLimit}
                            onChange={(e) => setUsageLimit(e.target.value)}
                            placeholder={isArabic ? "عدد المرات الكلي للاستخدام" : "Total times coupon can be used"}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "حد الاستخدام لكل مستخدم" : "Usage Limit Per User"}
                            type="number"
                            value={usageLimitPerUser}
                            onChange={(e) => setUsageLimitPerUser(e.target.value)}
                            placeholder={isArabic ? "مثال: 1" : "e.g: 1"}
                        />
                    </Grid>

                    {/* Validity Section */}
                    <Grid item xs={12}>
                        <Typography variant="overline" color="text.secondary" fontWeight="bold">
                            {isArabic ? "صلاحية الكوبون" : "Coupon Validity"}
                        </Typography>
                        <Divider sx={{ mb: 3, mt: 1 }} />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <FormInput
                            label={isArabic ? "تاريخ البدء" : "Start Date"}
                            type="date"
                            name="starts_at"
                            value={startsAt}
                            onChange={(e) => setStartsAt(e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <FormInput
                            label={isArabic ? "تاريخ الانتهاء" : "End Date"}
                            type="date"
                            name="ends_at"
                            value={endsAt}
                            onChange={(e) => setEndsAt(e.target.value)}
                            required
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <InputLabel sx={{ fontWeight: 'bold', mb: 1 }}>
                            {isArabic ? "تفعيل الكوبون" : "Enable Coupon"}
                        </InputLabel>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 2,
                            py: 1,
                            border: '1px solid',
                            borderColor: theme => theme.palette.divider,
                            borderRadius: '12px',
                            backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.01)',
                        }}>
                            <Typography variant="subtitle2" fontWeight="bold">
                                {isActive ? (isArabic ? "نشط" : "Active") : (isArabic ? "غير نشط" : "Inactive")}
                            </Typography>
                            <Switch
                                checked={isActive}
                                onChange={(e) => setIsActive(e.target.checked)}
                                color="primary"
                            />
                        </Box>
                    </Grid>
                </Grid>

                <Divider sx={{ my: 1 }} />

                <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                        variant="outlined"
                        onClick={() => onSubmitSuccess && onSubmitSuccess()}
                        disabled={loadingSubmit}
                        sx={{ borderRadius: '12px', px: 4 }}
                    >
                        {isArabic ? "إلغاء" : "Cancel"}
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loadingSubmit}
                        sx={{
                            minWidth: 160,
                            borderRadius: '12px',
                            fontWeight: 'bold',
                            textTransform: 'none',
                            boxShadow: theme.shadows[4],
                        }}
                    >
                        {loadingSubmit ? (isArabic ? "جاري الحفظ..." : "Saving...") : (isEdit ? (isArabic ? "تحديث" : "Update") : (isArabic ? "إنشاء" : "Create"))}
                    </Button>
                </Box>
            </Box>
        </Paper>
    );
};

export default FormAddEditCoupon;
